import { Effect } from 'effect';

import { attemptStudio, runStudioSync, type StudioRuntimeError } from '../effect-runtime';
import { uint8ArrayToBlobPart } from './utils';

export function downloadBlob(
  bytes: Uint8Array | Blob,
  name: string,
  type = 'application/pdf',
): void {
  runStudioSync(downloadBlobEffect(bytes, name, type));
}

export function downloadBlobEffect(
  bytes: Uint8Array | Blob,
  name: string,
  type = 'application/pdf',
): Effect.Effect<void, StudioRuntimeError> {
  return Effect.scoped(
    Effect.gen(function* () {
      const resource = yield* Effect.acquireRelease(
        attemptStudio('download', 'browser-blob', () => {
          const blob =
            bytes instanceof Blob ? bytes : new Blob([uint8ArrayToBlobPart(bytes)], { type });
          return { url: URL.createObjectURL(blob), handedOff: false };
        }),
        (resource) =>
          attemptStudio('download', 'release-url', () => {
            if (!resource.handedOff) URL.revokeObjectURL(resource.url);
          }).pipe(Effect.ignore),
      );
      const anchor = yield* Effect.acquireRelease(
        attemptStudio('download', 'create-anchor', () => document.createElement('a')),
        (anchor) =>
          attemptStudio('download', 'remove-anchor', () => anchor.remove()).pipe(Effect.ignore),
      );
      // Keep accepted click and the finite browser-owned handoff atomic with
      // respect to interruption. Allocation failures still release the URL here.
      yield* Effect.gen(function* () {
        yield* attemptStudio('download', 'click-anchor', () => {
          anchor.href = resource.url;
          anchor.download = name;
          anchor.rel = 'noopener';
          anchor.style.display = 'none';
          document.body.appendChild(anchor);
          anchor.click();
        });
        yield* Effect.sleep('30 seconds').pipe(
          Effect.ensuring(
            attemptStudio('download', 'release-url', () => URL.revokeObjectURL(resource.url)).pipe(
              Effect.ignore,
            ),
          ),
          Effect.forkDetach({ startImmediately: true, uninterruptible: false }),
        );
        resource.handedOff = true;
      }).pipe(Effect.uninterruptible);
    }),
  );
}

/** Strip/replace the .pdf extension so tool outputs get clean derived names. */
export function derivedName(sourceName: string, suffix: string, extension = '.pdf'): string {
  return Effect.runSync(derivedNameEffect(sourceName, suffix, extension));
}

export function derivedNameEffect(
  sourceName: string,
  suffix: string,
  extension = '.pdf',
): Effect.Effect<string> {
  return Effect.sync(() => {
    const base = sourceName.replace(/\.pdf$/i, '') || 'document';
    return `${base}${suffix}${extension}`;
  });
}
